/**
 * Auth Store
 *
 * Центральное хранилище для управления аутентификацией.
 * Координирует работу с access tokens через apiClient и localStorage.
 *
 * Ответственности:
 * - Хранение текущего tournamentId
 * - Управление токенами (login/logout)
 * - Проверка наличия доступа
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';

import { apiClient } from '@/api/client';

export const useAuthStore = defineStore('auth', () => {
	// State
	const currentTournamentId = ref<number | null>(null);

	// Actions

	/**
	 * Сохранить access token для турнира в localStorage
	 *
	 * @param tournamentId - ID турнира
	 * @param token - Access token
	 */
	function saveTournamentToken(tournamentId: number, token: string): void {
		apiClient.saveAccessToken(tournamentId, token);
		currentTournamentId.value = tournamentId;
	}

	/**
	 * Удалить access token для турнира
	 *
	 * @param tournamentId - ID турнира (опционально, по умолчанию текущий)
	 */
	function logout(tournamentId?: number): void {
		const id = tournamentId ?? currentTournamentId.value;
		if (!id) return;

		apiClient.removeAccessToken(id);
		if (id === currentTournamentId.value) currentTournamentId.value = null;
	}

	/**
	 * Проверить наличие access token для турнира
	 *
	 * @param tournamentId - ID турнира
	 * @returns true если токен есть
	 */
	function hasAccess(tournamentId: number): boolean {
		return !!apiClient.getAccessToken(tournamentId);
	}

	/**
	 * Установить текущий турнир
	 *
	 * @param tournamentId - ID турнира
	 */
	function setCurrentTournament(tournamentId: number): void {
		currentTournamentId.value = tournamentId;
	}

	return {
		// State
		currentTournamentId,

		// Actions
		saveTournamentToken,
		logout,
		hasAccess,
		setCurrentTournament
	};
});
